import { AsyncStatus, Satisfaction } from 'lib-shared-legacy-types';
import queryString from 'query-string';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Language } from 'app/utils/supportedLanguages';
import { Csat } from 'components/settings/components/sections/satisfaction/components/common/types';
import { DixaMessengerContactPointsLevel } from 'components/settings/reducers/sections/satisfaction';
import { useMessengers } from 'hooks/useMessengers';

import {
  DixaMessengerCsatSettingTabs,
  DixaMessengerPage,
} from '../page/DixaMessengerPage';
import { DixaMessengerCSAT } from '../page/types';

export interface MessengerSetting {
  id: string;
  name: string;
  enabled: boolean;
  settings: Satisfaction.DixaMessengerCsatSettings | null;
}

export interface LanguageSetting extends Language {
  settings: Satisfaction.DixaMessengerCsatSettings | null;
}

export interface DispatchProps {
  loadDixaMessengerSettingsAllLevels: () => void;
  initializeDixaMessengerCsat: () => void;
  createGlobalSettings: (
    settings: Satisfaction.CreateDixaMessengerCsatSettingsParams,
  ) => void;
  updateGlobalSettings: (
    settings: Satisfaction.UpdateDixaMessengerCsatSettingsParams,
  ) => void;
  updateContactPointLevelSettings: (
    settings: DixaMessengerCSAT.MessengerStatusChange,
  ) => void;
  updateLanguageLevelSettings: (
    settings: Partial<Satisfaction.DixaMessengerCsatSettings>,
  ) => void;
}

export interface StateProps {
  globalSettings: {
    values: Satisfaction.DixaMessengerCsatSettings | null;
    asyncStatus: AsyncStatus;
  };

  contactPointSettings: {
    list: Readonly<DixaMessengerContactPointsLevel['values']>;
    asyncStatus: AsyncStatus;
  };

  languagesSettings: {
    list: LanguageSetting[];
    asyncStatus: AsyncStatus;
  };
}

type InitiallyActiveTabQueryParamName = 'initiallyActiveTab';
export const initiallyActiveTabQueryParamName: InitiallyActiveTabQueryParamName =
  'initiallyActiveTab' as const;

type QueryT = {
  [key in InitiallyActiveTabQueryParamName]:
    | string
    | string[]
    | null
    | undefined;
};

type Props = DispatchProps & StateProps;

DixaMessengerPanel.PAGE = 'messenger';

export function DixaMessengerPanel({
  loadDixaMessengerSettingsAllLevels,
  globalSettings,
  contactPointSettings,
  languagesSettings,
  updateGlobalSettings,
  initializeDixaMessengerCsat,
  updateLanguageLevelSettings,
  updateContactPointLevelSettings,
}: Props): JSX.Element {
  const navigate = useNavigate();

  const { items, asyncStatus } = useMessengers({ limit: 200 });

  useEffect(() => {
    loadDixaMessengerSettingsAllLevels();
  }, [loadDixaMessengerSettingsAllLevels]);

  const messengers = items.map<DixaMessengerCSAT.MessengerListSetting>(
    ({ id, name }) => {
      const correspondingSettings = contactPointSettings.list?.find(
        ({ contactPoint }) => contactPoint === id,
      );

      if (correspondingSettings) {
        return {
          id,
          name,
          enabled: correspondingSettings.enabled,
          settings: correspondingSettings,
        };
      }

      return {
        id,
        name,
        enabled: false,
        settings: null,
      };
    },
  );

  function getEnrichedLanguagesWithSettings(): DixaMessengerCSAT.Language[] {
    return languagesSettings.list.map(
      ({ code, label, settings }): DixaMessengerCSAT.Language => ({
        isSetup: settings !== null,
        ratingStyle: settings?.customization.look_and_feel,
        enabled: settings?.enabled || false,
        name: label,
        code,
        settings,
      }),
    );
  }

  function redirectToLanguageCustomizationPage(
    language: DixaMessengerCSAT.Language,
  ): void {
    const pathname = language.isSetup
      ? `/settings/satisfaction/${DixaMessengerPanel.PAGE}/${language.code}/edit/${language.settings?.id}`
      : `/settings/satisfaction/${DixaMessengerPanel.PAGE}/${language.code}/create/`;

    navigate(pathname);
  }

  function handleGlobalStatusChange(change: Csat.CsatStatusChange): void {
    // If we have global settings already, update it
    if (globalSettings.values) {
      updateGlobalSettings({
        ...globalSettings.values,
        enabled: change.newStatus,
      });
    } else {
      // Otherwise csat is not initialized, so initialize if needed.
      if (change.newStatus === true) {
        initializeDixaMessengerCsat();
      }
    }
  }

  function handleLanguageStatusChange({
    language,
    newStatus,
  }: DixaMessengerCSAT.LanguageCustomizationStatusChange): void {
    // the default language should not be allowed to be disabled.
    if (DixaMessengerCSAT.defaultLanguage === language.code) {
      return;
    }

    const payload: Partial<Satisfaction.DixaMessengerCsatSettings> = {
      id: language.settings?.id,
      enabled: newStatus,
      channel: 'email',
    };

    updateLanguageLevelSettings(payload);
  }

  function isDixaMessengerCsatSetupGlobally(): boolean {
    return Boolean(globalSettings.values);
  }

  function isDixaMessengerCsatActiveGlobally(): boolean {
    return globalSettings.values?.enabled || false;
  }

  function getInitiallyActiveTab(): DixaMessengerCsatSettingTabs {
    let { initiallyActiveTab } = queryString.parse(location.search) as QueryT;

    if (Array.isArray(initiallyActiveTab)) {
      initiallyActiveTab = initiallyActiveTab[0];
    }

    const cleanedUpValue = initiallyActiveTab?.trim().toLowerCase();

    if (!cleanedUpValue) {
      return DixaMessengerCsatSettingTabs.Messengers;
    }

    switch (cleanedUpValue) {
      case DixaMessengerCsatSettingTabs.Messengers.toLowerCase():
        return DixaMessengerCsatSettingTabs.Messengers;

      case DixaMessengerCsatSettingTabs.LanguageLevelCustomizations.toLowerCase():
        return DixaMessengerCsatSettingTabs.LanguageLevelCustomizations;

      default:
        return DixaMessengerCsatSettingTabs.Messengers;
    }
  }

  return (
    <DixaMessengerPage
      existingMessengers={messengers}
      languages={getEnrichedLanguagesWithSettings()}
      onLanguageEditRequest={redirectToLanguageCustomizationPage}
      onMessengerStatusChange={updateContactPointLevelSettings}
      onGlobalStatusChange={handleGlobalStatusChange}
      onLanguageStatusChange={handleLanguageStatusChange}
      isLoadingMessengersList={asyncStatus === 'loading'}
      isMessengerCsatActive={isDixaMessengerCsatActiveGlobally()}
      isMessengerCsatSetup={isDixaMessengerCsatSetupGlobally()}
      initiallyActiveTab={getInitiallyActiveTab()}
    />
  );
}
